import { useCallback, useEffect, useRef, useState } from 'react'

type UseVisibleLineCountOptions = {
  /** 最低でもこの行数は表示する */
  minLines: number
  /** 文字1行分の高さ(px)。表示に使うスタイルと同じ値を渡すこと */
  lineHeightPx: number
}

type UseVisibleLineCountResult<T extends HTMLElement> = {
  /** 「表示に使える高さ」を測る要素に付ける取っ手 */
  measureRef: React.RefObject<T>
  /** 実際に文章を切っている要素に付ける取っ手 */
  textRef: React.RefObject<T>
  /** 何行まで表示するか */
  visibleLineCount: number
  /** いま文章が切れているかどうかを、その場で調べる */
  measureIsTextCut: () => boolean
}

/** 与えられた高さに何行入るかを求める。ただし最低 minLines 行は必ず確保する */
export const calculateVisibleLineCount = (availableHeightPx: number, lineHeightPx: number, minLines: number) => {
  if (lineHeightPx <= 0) {
    return minLines
  }

  // 測った高さには小数の誤差が乗るため、その分だけ余裕を持たせてから割る
  return Math.max(minLines, Math.floor((availableHeightPx + 0.5) / lineHeightPx))
}

/**
 * 「最低 minLines 行、表示に使える高さに余裕があればその高さいっぱいまで」何行表示するかを求めるフック。
 *
 * 表示に使える高さを実際に測り、1行の高さで割って行数を求めている。
 * 高さが変わったら測り直せるよう、要素の大きさの変化を監視している。
 *
 * 測る要素(measureRef)と文章を切る要素(textRef)が分かれているのは、
 * 高さを測る側は「浮かせた要素」にする必要がある一方、
 * 浮かせた要素では文章を切る CSS が効かなくなるというブラウザの制約があるため。
 */
export const useVisibleLineCount = <T extends HTMLElement>({
  minLines,
  lineHeightPx,
}: UseVisibleLineCountOptions): UseVisibleLineCountResult<T> => {
  const measureRef = useRef<T>(null)
  const textRef = useRef<T>(null)
  const [availableHeightPx, setAvailableHeightPx] = useState(0)

  useEffect(() => {
    const element = measureRef.current
    if (!element || typeof ResizeObserver === 'undefined') {
      return
    }

    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (entry) {
        setAvailableHeightPx(entry.contentRect.height)
      }
    })
    observer.observe(element)

    return () => observer.disconnect()
  }, [])

  // 切れているかどうかは行数が確定した後の表示結果にしか現れないので、
  // 状態としては持たず、知りたくなった時点で測る
  const measureIsTextCut = useCallback(() => {
    const element = textRef.current
    if (!element) {
      return false
    }

    // 中身の全体の高さが、実際に見えている高さより大きければ、はみ出した分が切られている
    return element.scrollHeight > element.clientHeight + 1
  }, [])

  return {
    measureRef,
    textRef,
    visibleLineCount: calculateVisibleLineCount(availableHeightPx, lineHeightPx, minLines),
    measureIsTextCut,
  }
}
